import React, { forwardRef } from 'react';

import { InputAdornment, OutlinedInputProps } from '@mui/material';
import { CoreInput, CoreInputProps } from '../../core-input';
import ClearIcon from '@mui/icons-material/Clear';
import { CoreIconButton } from '../../core-button';
import { CoreFormControl, CoreFormControlProps } from '../../core-form-control';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import { CalendarIcon } from '../../../icons';
import { mergeSx } from 'merge-sx';
import dayjs from 'dayjs';

export interface CalendarInputProps extends Omit<OutlinedInputProps, 'label'>, CoreFormControlProps {
	onClear?: () => void;
	showFilterIcon?: boolean;
	selectsRange?: boolean;
}

const CalendarInput = forwardRef<HTMLInputElement, CoreInputProps & CalendarInputProps>((props, ref) => {
	const {
		value,
		onClear,
		id,
		fullWidth,
		controlSx,
		label,
		helperText,
		selectsRange,
		error,
		disabled,
		requiredMark,
		showFilterIcon = true,
		customLabelSx,
	} = props;

	return (
		<CoreFormControl
			disabled={disabled}
			error={error}
			helperText={helperText}
			label={label}
			id={id}
			requiredMark={requiredMark}
			fullWidth={fullWidth}
			controlSx={controlSx}
			customLabelSx={customLabelSx}
		>
			<CoreInput
				{...props}
				sx={mergeSx(
					{
						paddingRight: '16px',
						margin: 0,
						'& .MuiOutlinedInput-input': {
							minWidth: '175px',
							paddingRight: 0,
						},
						'& .MuiInputAdornment-root': {
							color: (theme) => theme.colors.gray,
						},
						'&.Mui-focused .MuiInputAdornment-root': {
							color: (theme) => theme.colors.lightBlue,
						},
						'&.Mui-disabled .MuiInputAdornment-root': {
							color: (theme) => theme.colors.blueTransparent,
						},
						'&.MuiOutlinedInput-root': {
							paddingRight: showFilterIcon ? '16px' : 0,
						},
					},
					props.sx
				)}
				inputRef={ref}
				placeholder={selectsRange ? 'Choose Date Range' : 'Choose Date'}
				value={
					typeof value === 'string'
						? value
						: Array.isArray(value)
						? value
						: value instanceof Date
						? dayjs(value).format('MM/DD/YYYY')
						: ''
				}
				endAdornment={
					<InputAdornment position="end">
						<>
							{value && (
								<CoreIconButton
									onClick={onClear}
									sx={{
										p: `${props?.small ? 3 : 6}px`,
										marginRight: '4px',
										color: ({ colors }) => colors.gray,
									}}
								>
									<ClearIcon />
								</CoreIconButton>
							)}
							{showFilterIcon && <CalendarIcon />}
						</>
					</InputAdornment>
				}
			/>
		</CoreFormControl>
	);
});

export default CalendarInput;
